import { useMemo } from "react";
import { useIntl } from "react-intl";
import { Box, Container, IconButton } from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";

import TipTapEditor from "./TipTapEditor";
import { BannerType, TipTapNode } from "./interfaces";
import { getBannerTextColor } from "./utils";
import { getCurrentLocale, Locale } from "../Locale";

const DEFAULT_LOCALE = Locale.EN_GB;

const resolveIntlNodes = (
  node: TipTapNode,
  formatMessage: (descriptor: { id: string }) => string
): TipTapNode => {
  if (node.type === "intl" && node.text) {
    return { ...node, type: "text", text: formatMessage({ id: node.text }) };
  }
  if (node.content) {
    return {
      ...node,
      content: node.content.map((child) =>
        resolveIntlNodes(child, formatMessage)
      ),
    };
  }
  return node;
};

const updateIntlContent = (
  banner: BannerType,
  formatMessage: (descriptor: { id: string }) => string
): BannerType => {
  const resolvedContent = Object.fromEntries(
    Object.entries(banner.content).map(([locale, node]) => [
      locale,
      resolveIntlNodes(node, formatMessage),
    ])
  ) as BannerType["content"];
  return { ...banner, content: resolvedContent };
};

const Banner = ({
  onCloseBanner,
  banner,
}: {
  onCloseBanner: () => void;
  banner: BannerType;
}) => {
  const { formatMessage } = useIntl();

  const banerContent = useMemo(() => {
    const locale = getCurrentLocale();
    const resolved = updateIntlContent(banner, formatMessage);

    return resolved.content[locale] ?? resolved.content[DEFAULT_LOCALE];
  }, [banner, formatMessage]);

  return (
    <Container
      maxWidth="xl"
      sx={{ marginBottom: 1, position: "sticky", top: "8px", zIndex: 3 }}
    >
      <Box sx={{ position: "relative" }}>
        <TipTapEditor banner={banner} content={banerContent} />
        {!banner.is_pinned && (
          <IconButton
            sx={(theme) => ({
              position: "absolute",
              top: "4px",
              right: "4px",
              color: getBannerTextColor(theme, banner.criticity),
            })}
            onClick={onCloseBanner}
            aria-label="Close banner"
          >
            <ClearIcon />
          </IconButton>
        )}
      </Box>
    </Container>
  );
};

export default Banner;
